import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { SegmentAirlineInfo } from "@b2bportal/air-booking-api";
import { FlightPostBookingSelectors } from "@b2bportal/core-flights";
import {
  ScheduleChangeModalState,
  SliceDirection,
  type TimeGroupingEnum,
} from "@b2bportal/core-types";
import {
  flightsBookingApi,
  getScheduleChangePrefs,
  SCHEDULE_CHANGE_DENY_CONTACT_AIRLINE_HOURS,
  selfServeApi,
} from "@b2bportal/core-utilities";
import { EncodeBookingResponseEnum, Product } from "@b2bportal/ivr-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { ScheduleChangeUserActionEnum } from "@hopper-b2b/types";
import type { IFlightScheduleChangeModalContentProps } from "@tenant/flights";
import { uniqBy } from "lodash-es";

export const useFlightScheduleChangeModal = (
  props: IFlightScheduleChangeModalContentProps
) => {
  const {
    closeModal,
    hoursToDeparture = 0,
    itinerary,
    scheduleChangeDiffs,
  } = props;
  const { formatDateTime, t } = useI18nContext();
  const airlines = useSelector(FlightPostBookingSelectors.getAirlines);
  const airports = useSelector(FlightPostBookingSelectors.getAirports);
  const [modalState, setModalState] = useState(
    ScheduleChangeModalState.details
  );
  const [outboundDepDate, setOutboundDepDate] = useState<string>();
  const [returnDepDate, setReturnDepDate] = useState<string>();
  const [supportId, setSupportId] = useState<string>();

  const acceptScheduleChange = useCallback(async () => {
    try {
      const res =
        await flightsBookingApi.apiV0ItineraryFlightAcceptScheduleChangePost({
          itineraryId: itinerary.id,
        });

      if (res.data.success) {
        setModalState(ScheduleChangeModalState.acceptSuccess);
      } else {
        setModalState(ScheduleChangeModalState.contactSupport);
      }
    } catch {
      setModalState(ScheduleChangeModalState.contactSupport);
    }
  }, [itinerary]);

  const dateFormatter = useCallback(
    (date: string, style = DateTimeFormatStyle.ShortMonthDayShortWeekday) => {
      return formatDateTime(new Date(date), style);
    },
    [formatDateTime]
  );

  const denyScheduleChange = useCallback(
    async (
      outboundPrefs: TimeGroupingEnum[],
      returnPrefs: TimeGroupingEnum[]
    ) => {
      const payload = {
        itineraryId: itinerary.id,
        outboundPreferences:
          outboundDepDate != null
            ? getScheduleChangePrefs(outboundPrefs, outboundDepDate)
            : [],
        returnPreferences:
          returnDepDate != null
            ? getScheduleChangePrefs(returnPrefs, returnDepDate)
            : [],
      };

      try {
        await flightsBookingApi.apiV0ItineraryFlightDenyScheduleChangePost(
          payload
        );
      } finally {
        setModalState(ScheduleChangeModalState.contactSupport);
      }
    },
    [itinerary, outboundDepDate, returnDepDate]
  );

  const fetchSupportId = useCallback(async () => {
    try {
      const { data: supportIdRes } = await selfServeApi.apiV0IvrEncodePost({
        bookingId: itinerary.id,
        product: Product.Flight,
      });

      if (
        supportIdRes.EncodeBookingResponse === EncodeBookingResponseEnum.Success
      ) {
        setSupportId(supportIdRes.encodedCode);
      }
    } catch (err) {
      console.error(err);
    }
  }, [itinerary]);

  const onFormSubmit = useCallback(
    async (
      userAction: ScheduleChangeUserActionEnum,
      outboundTimes: TimeGroupingEnum[],
      returnTimes: TimeGroupingEnum[]
    ) => {
      setModalState(ScheduleChangeModalState.processing);

      if (userAction === ScheduleChangeUserActionEnum.Accept) {
        await acceptScheduleChange();
      } else if (
        hoursToDeparture < SCHEDULE_CHANGE_DENY_CONTACT_AIRLINE_HOURS
      ) {
        setModalState(ScheduleChangeModalState.contactAirline);
      } else {
        await denyScheduleChange(outboundTimes, returnTimes);
      }
    },
    [acceptScheduleChange, denyScheduleChange, hoursToDeparture]
  );

  const visitAirline = useCallback(
    (airlineCode: string) => {
      const airlineWebLinks = airlines[airlineCode].webLinks;
      const { homePage, manageBooking } = airlineWebLinks;
      const visitUrl = manageBooking ?? homePage;

      if (visitUrl != null) {
        window.open(visitUrl, "_blank");
      }
    },
    [airlines]
  );

  const changedSlicesAirlines = useMemo(() => {
    const { scheduleChange } = itinerary;
    const airlines: SegmentAirlineInfo[] = [];

    if (scheduleChange?.next != null) {
      for (const changedSlice of scheduleChange.next) {
        const { marketingAirline } = changedSlice.segments[0];

        if (marketingAirline.code != null) {
          airlines.push(marketingAirline);
        }
      }
    }

    return uniqBy(airlines, "code");
  }, [itinerary]);

  useEffect(() => {
    fetchSupportId();
  }, [fetchSupportId]);

  useEffect(() => {
    const sliceChanges = Object.values(scheduleChangeDiffs.changes);

    for (const sliceChange of sliceChanges) {
      const { scheduledDeparture, zonedScheduledDeparture } =
        sliceChange.updatedSlice.segments[0];
      const scheduledDepDate = zonedScheduledDeparture ?? scheduledDeparture;

      if (sliceChange.direction === SliceDirection.Outbound) {
        setOutboundDepDate(scheduledDepDate);
      } else if (sliceChange.direction === SliceDirection.Return) {
        setReturnDepDate(scheduledDepDate);
      }
    }
  }, [scheduleChangeDiffs]);

  return useMemo(
    () => ({
      context: {
        airports,
        changedSlicesAirlines,
        modalState,
        supportId,
        outboundChanged: outboundDepDate != null,
        returnChanged: returnDepDate != null,
      },
      handlers: {
        closeModal,
        dateFormatter,
        onFormSubmit,
        visitAirline,
      },
      text: {
        acceptSuccess: {
          body: t("core-flights.postbooking.scheduleChange.acceptSuccess.body"),
          doneBtn: t("done"),
          title: t(
            "core-flights.postbooking.scheduleChange.acceptSuccess.title"
          ),
        },
        contactAirline: {
          airlineLocatorLabel: (airlineCode: string) =>
            t(
              "core-flights.postbooking.scheduleChange.contactAirline.airlineLocatorLabel",
              { airlineName: airlines[airlineCode].displayName }
            ),
          body: t(
            "core-flights.postbooking.scheduleChange.contactAirline.body",
            {
              airlineName: airlines[changedSlicesAirlines[0].code].displayName,
              count: changedSlicesAirlines.length,
            }
          ),
          closeBtn: t(
            "core-flights.postbooking.scheduleChange.contactAirline.closeBtn"
          ),
          title: t(
            "core-flights.postbooking.scheduleChange.contactAirline.title"
          ),
          visitAirlineBtn: (airlineCode: string) =>
            t(
              "core-flights.postbooking.scheduleChange.contactAirline.visitAirlineBtn",
              { airlineName: airlines[airlineCode].displayName }
            ),
        },
        details: {
          body: t("scheduleChange.majorChangeModalDescription", {
            expDate: dateFormatter(scheduleChangeDiffs.expiry),
          }),
          originalSliceTitle: (direction: SliceDirection) => {
            if (direction === SliceDirection.Outbound) {
              return t("scheduleChange.originalFlight", {
                context: "outbound",
              });
            }

            return t("scheduleChange.originalFlight", {
              context: "return",
            });
          },
          title: t("scheduleChange.reviewScheduleChange"),
          updatedSliceTitle: (direction: SliceDirection) => {
            if (direction === SliceDirection.Outbound) {
              return t("scheduleChange.updatedFlight", {
                context: "outbound",
              });
            }

            return t("scheduleChange.updatedFlight", {
              context: "return",
            });
          },
        },
        dialogTriggerBtnLabel: t(
          "core-flights.itinerary.card.notificationBanner.reviewScheduleChangeBtn"
        ),
        processing: {
          title: t("core-flights.postbooking.scheduleChange.processing"),
        },
        contactSupport: {
          title: t("scheduleChangeRequestedModal.contactSupportTitle"),
          subtitle: t("scheduleChangeRequestedModal.contactSupportSubtitle", {
            expiryDate: dateFormatter(
              scheduleChangeDiffs.expiry,
              DateTimeFormatStyle.ShortMonthDayShortWeekdayTime
            ),
          }),
        },
      },
    }),
    [
      airlines,
      airports,
      changedSlicesAirlines,
      closeModal,
      dateFormatter,
      modalState,
      onFormSubmit,
      outboundDepDate,
      returnDepDate,
      scheduleChangeDiffs.expiry,
      supportId,
      t,
      visitAirline,
    ]
  );
};

export type IFlightScheduleChangeModalData = ReturnType<
  typeof useFlightScheduleChangeModal
>;
